import { ButtonBase } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Stack, styled, Text } from 'shell/components';

const QuestionSettingsNavigation = ({ device, questions, isResyncing, onResync, onSelect }) => {
    const { t } = useTranslation('quiz');
    const isMobile = device === 'mobile';

    return (
        <QuestionSidebar component={'aside'} $device={device} spacing={5}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} spacing={3}>
                <Text component={'h2'} variant={'sh1'} weight={'medium'}>
                    {isMobile
                        ? t('create.questions-with-count', 'Questions ({{count}})', { count: questions.length })
                        : t('create.questions', 'Questions')}
                </Text>
                <Button
                    variant={'borderless'}
                    size={'small'}
                    startIcon={<Icon kind={'refresh'} />}
                    loading={isResyncing}
                    disabled={isResyncing}
                    onClick={onResync}
                >
                    {t('create.resync', 'Resync from LMS')}
                </Button>
            </Stack>
            {!isMobile && (
                <Text color={'light'}>
                    {t(
                        'create.question-settings-description',
                        'These questions are only for grading in TimelyGrader, and you can add grading notes for each.'
                    )}
                </Text>
            )}
            {!isMobile && (
                <Stack component={'nav'} aria-labelledby={'subjective-question-navigation-heading'} spacing={3}>
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                        <Text id={'subjective-question-navigation-heading'} weight={'medium'}>
                            {t('create.subjective', 'Subjective Questions')}
                        </Text>
                        <Text color={'light'}>({questions.length})</Text>
                    </Stack>
                    <Stack spacing={3}>
                        {questions.map((question, index) => (
                            <QuestionNavigationButton
                                key={question.id}
                                aria-controls={`quiz-question-settings-${question.id}`}
                                onClick={() => onSelect(question.id)}
                            >
                                <NavigationButtonContent alignItems={'flex-start'} direction={'row'} spacing={1}>
                                    <QuestionNumber weight={'medium'}>
                                        {t('create.question-number-short', 'Q{{number}})', { number: index + 1 })}
                                    </QuestionNumber>
                                    <QuestionNavigationText rich={question.rich}>
                                        {question.content ||
                                            t('create.question-missing-content', 'Question text is unavailable.')}
                                    </QuestionNavigationText>
                                </NavigationButtonContent>
                            </QuestionNavigationButton>
                        ))}
                    </Stack>
                </Stack>
            )}
        </QuestionSidebar>
    );
};

const QuestionSidebar = styled(Stack, {
    shouldForwardProp: (prop) => prop !== '$device',
})(({ $device, theme }) => ({
    width: $device === 'desktop' ? '350px' : $device === 'tablet' ? '280px' : '100%',
    flexShrink: 0,
    alignSelf: 'stretch',
    padding: theme.spacing(5),
    background: theme.palette.ensightful.neutral[100],
    ...($device !== 'mobile' && {
        position: 'sticky',
        top: theme.spacing(15),
        maxHeight: `calc(100vh - ${theme.spacing(15)})`,
        overflowY: 'auto',
    }),
}));

const QuestionNavigationButton = styled(ButtonBase)(({ theme }) => ({
    borderRadius: '16px',
    width: '100%',
    height: '56px',
    padding: theme.spacing(3),
    overflow: 'hidden',
    boxShadow: theme.palette.ensightful.shadows[1],
    background: theme.palette.ensightful.shades[0],
    '&:hover': {
        background: theme.palette.ensightful.primary[50],
    },
    '&:active': {
        background: theme.palette.ensightful.primary[100],
    },
    '&:focus-visible': {
        outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
        outlineOffset: '2px',
    },
}));

const NavigationButtonContent = styled(Stack)(() => ({ width: '100%' }));
const QuestionNumber = styled(Text)(() => ({ flexShrink: 0 }));
const QuestionNavigationText = styled(Text)(() => ({
    minWidth: 0,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    '& p': { margin: 0 },
}));

export default QuestionSettingsNavigation;
